import React, { useEffect, useState } from "react";
import { Card, Row, Col, Tooltip, Alert, Typography } from "antd";
import ReactApexChart from "react-apexcharts";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { useGetMonthlyComparisonQuery } from "../../../Redux/service/fsm/FSMDashboard";
import Loader from "../../Helpers/Loader/Loader";
import trans from "../../../utils";
import SectionTitle from "./widgets/SectionTitle";
import NumbersCard from "./widgets/NumbersCard";
import dayjs from "dayjs";
import AlertPopup from "../FMSDashboardPopups/AlertPopup";

const { Title, Text } = Typography;

// Colors matching your dashboard style
const colorScheme = {
  primary: "#0f6936",
  primaryLight: "#16a34a",
  secondary: "#059669",
  warning: "#ea580c",
  info: "#0ea5e9",
  success: "#22c55e",
  neutral: "#64748b",
  light: "#f8fafc",
  dark: "#1e293b",
  surface: "#ffffff",
  border: "#e2e8f0",
  danger: "#dc2626",
};

export default function FSMMonthlyComparison({ refreshKey }) {
  const [selectedAlert, setSelectedAlert] = useState(null);
  const {
    data: monthlyData,
    isLoading,
    error,
    refetch,
  } = useGetMonthlyComparisonQuery(
    { refreshKey },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    refetch();
  }, []);

  if (isLoading) {
    return (
      <Card
        style={{
          borderRadius: "12px",
          border: `1px solid ${colorScheme.border}`,
          background: colorScheme.surface,
        }}
      >
        <div style={{ textAlign: "center", padding: "48px 24px" }}>
          <Loader />
          <Text
            style={{
              marginTop: "16px",
              color: colorScheme.neutral,
              fontSize: "14px",
              display: "block",
            }}
          >
            {trans("Loading monthly comparison...")}
          </Text>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        style={{
          borderRadius: "12px",
          border: `1px solid ${colorScheme.border}`,
        }}
      >
        <Alert
          message={trans("Failed to load monthly comparison")}
          type="error"
          showIcon
          style={{ borderRadius: "6px" }}
        />
      </Card>
    );
  }

  const {
    tasks = {},
    completions = {},
    serviceRequests = {},
    avgDuration = {},
  } = monthlyData || {};

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;

    const totalHours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (totalHours < 24) {
      return mins > 0 ? `${totalHours}h ${mins}m` : `${totalHours}h`;
    }

    const days = Math.floor(totalHours / 24);
    const remainingHours = totalHours % 24;

    if (remainingHours === 0 && mins === 0) {
      return `${days}d`;
    } else if (mins === 0) {
      return `${days}d ${remainingHours}h`;
    } else if (remainingHours === 0) {
      return `${days}d ${mins}m`;
    } else {
      return `${days}d ${remainingHours}h ${mins}m`;
    }
  };

  const getTrendIcon = (difference, isPositiveGood = true) => {
    if (difference === 0) return null;
    const isActuallyPositive = isPositiveGood ? difference > 0 : difference < 0;
    return isActuallyPositive ? (
      <ArrowUpOutlined
        style={{ color: colorScheme.success, fontSize: "12px" }}
      />
    ) : (
      <ArrowDownOutlined
        style={{ color: colorScheme.danger, fontSize: "12px" }}
      />
    );
  };

  const getTrendColor = (difference, isPositiveGood = true) => {
    if (difference === 0) return colorScheme.neutral;
    const isActuallyPositive = isPositiveGood ? difference > 0 : difference < 0;
    return isActuallyPositive ? colorScheme.success : colorScheme.danger;
  };

  const getPercentageChangeText = (percentageChange) => {
    const change = parseFloat(percentageChange);
    if (change === 0) return "0%";
    return `${change > 0 ? "+" : ""}${change.toFixed(1)}%`;
  };

  const comparisonItems = [
    {
      key: "tasks",
      title: trans("Tasks Created"),
      current: tasks.current || 0,
      previous: tasks.previous || 0,
      difference: tasks.difference || 0,
      percentageChange: tasks.percentageChange || "0",
      icon: <CheckCircleOutlined />,
      color: colorScheme.primary,
      isPositiveGood: true,
      description: trans("Total tasks created this month vs last month"),
      tooltip: trans("Total number of tasks created this month."),
      api: "useGetAllTasksQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Task Name"), dataIndex: "name", width: 200 },
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedTo",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Priority"), dataIndex: "priority", width: 100 },
        { title: trans("Status"), dataIndex: "status", width: 100 },
      ],
      query: {
        page: 1,
        limit: 10,
        from: dayjs().startOf("month").format("YYYY-MM-DDTHH:mm:ss"),
        to: dayjs().endOf("month").format("YYYY-MM-DDTHH:mm:ss"),
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      key: "completions",
      title: trans("Tasks Completed"),
      current: completions.current || 0,
      previous: completions.previous || 0,
      difference: completions.difference || 0,
      percentageChange: completions.percentageChange || "0",
      icon: <TrophyOutlined />,
      color: colorScheme.success,
      isPositiveGood: true,
      tooltip: trans(
        "Number of tasks successfully completed during the month."
      ),
      description: trans("Tasks completed this month vs last month"),
      api: "useGetAllTasksQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Task Name"), dataIndex: "name", width: 200 },
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedTo",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Priority"), dataIndex: "priority", width: 100 },
        { title: trans("Status"), dataIndex: "status", width: 100 },
      ],
      query: {
        page: 1,
        limit: 10,
        from: dayjs().startOf("month").format("YYYY-MM-DDTHH:mm:ss"),
        to: dayjs().endOf("month").format("YYYY-MM-DDTHH:mm:ss"),
        status: "done",
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      key: "serviceRequests",
      title: trans("Service Requests"),
      current: serviceRequests.current || 0,
      previous: serviceRequests.previous || 0,
      difference: serviceRequests.difference || 0,
      percentageChange: serviceRequests.percentageChange || "0",
      icon: <FileTextOutlined />,
      color: colorScheme.info,
      isPositiveGood: true,
      tooltip: trans("Total service requests received within the month."),
      description: trans("New service requests this month vs last month"),
      api: "useGetRequestsByStatusQuery",
      extractData: (response) => response?.data?.rows || [],
      frontendFilter: null,
      columns: [
        { title: trans("Service Type"), dataIndex: "serviceType", width: 150 },
        {
          title: trans("Requester (Customer)"),
          dataIndex: "requestedBy",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        {
          title: trans("Preferred Date"),
          dataIndex: "preferredTime",
          width: 150,
          render: (value) =>
            value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -",
        },
        {
          title: trans("Created At"),
          dataIndex: "_metadata",
          width: 150,
          render: (value) =>
            value?.createdAt
              ? dayjs(value?.createdAt).format("DD/MM/YYYY")
              : "- - - - -",
        },
      ],
      query: {
        val: {
          page: 1,
          limit: 10,
          fromDate: dayjs().startOf("month").format("YYYY-MM-DDTHH:mm:ss"),
          toDate: dayjs().endOf("month").format("YYYY-MM-DDTHH:mm:ss"),
        },
        status: "completed",
      },
      extractTotal: (response) => response?.data?.pagination?.totalCount || 0,
    },
    {
      key: "avgDuration",
      title: trans("Avg Duration"),
      current: avgDuration.current || 0,
      previous: avgDuration.previous || 0,
      difference: avgDuration.difference || 0,
      percentageChange: "0",
      icon: <ClockCircleOutlined />,
      color: colorScheme.warning,
      isPositiveGood: false,
      tooltip: "Average time taken to complete all tasks.",
      description: trans("Average task duration this month vs last month"),
      isTime: true,
    },
  ];

  const getPerformanceSummary = () => {
    const improvements = comparisonItems.filter((item) => {
      if (item.isTime) {
        return item.difference <= 0;
      }
      return item.difference >= 0;
    }).length;

    return {
      improvements,
      total: comparisonItems.length,
      percentage: Math.round((improvements / comparisonItems.length) * 100),
    };
  };

  const performanceSummary = getPerformanceSummary();

  // ApexCharts configurations
  const barChartOptions = {
    chart: {
      type: "bar",
      fontFamily: "'Cairo', sans-serif !important",
      toolbar: { show: false },
    },
    colors: [colorScheme.neutral, colorScheme.primary],
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: "60%",
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: comparisonItems.map((item) => item.title),
      labels: {
        style: { colors: colorScheme.neutral, fontSize: "11px" },
        rotate: -45,
      },
    },
    yaxis: {
      labels: {
        style: { colors: colorScheme.neutral, fontSize: "11px" },
      },
    },
    grid: {
      borderColor: colorScheme.border,
      strokeDashArray: 3,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      labels: { colors: colorScheme.dark, fontSize: "12px" },
    },
    tooltip: {
      y: {
        formatter: function (val, opts) {
          const isTime = comparisonItems[opts.dataPointIndex]?.isTime;
          return isTime ? formatDuration(val) : val.toLocaleString();
        },
      },
    },
  };

  const lineChartOptions = {
    chart: {
      type: "line",
      fontFamily: "'Cairo', sans-serif !important",
      toolbar: { show: false },
    },
    colors: comparisonItems.map((item) => item.color),
    stroke: {
      curve: "smooth",
      width: 3,
    },
    markers: {
      size: 6,
      strokeColors: "#fff",
      strokeWidth: 2,
    },
    xaxis: {
      categories: ["Last Month", "This Month"],
      labels: {
        style: { colors: colorScheme.neutral, fontSize: "11px" },
      },
    },
    yaxis: {
      labels: {
        style: { colors: colorScheme.neutral, fontSize: "11px" },
      },
    },
    grid: {
      borderColor: colorScheme.border,
      strokeDashArray: 3,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      labels: { colors: colorScheme.dark, fontSize: "12px" },
    },
    tooltip: {
      y: {
        formatter: function (val, opts) {
          const item = comparisonItems[opts.seriesIndex];
          return item?.isTime ? formatDuration(val) : val.toLocaleString();
        },
      },
    },
  };

  const barSeriesData = [
    {
      name: "Last Month",
      data: comparisonItems.map((item) => item.previous),
    },
    {
      name: "This Month",
      data: comparisonItems.map((item) => item.current),
    },
  ];

  const lineSeriesData = comparisonItems.map((item) => ({
    name: item.title,
    data: [item.previous, item.current],
  }));

  return (
    <div
      style={{
        minHeight: "100%",
        padding: "24px",
        borderRadius: "12px",
        border: `1px solid ${colorScheme.border}`,
        display: "flex", // 👈 enable flex
        flexDirection: "column", // 👈 stack children vertically
      }}
    >
      <SectionTitle
        title={"Monthly Performance"}
        subtitle={"Compare current month performance with previous month"}
        imgSrc={`${process.env.PUBLIC_URL}/assets/trendup.svg`}
        imgBgColor="#FFFBE6"
      />

      {/* Comparison Cards Grid */}
      <Row gutter={[16, 16]}>
        {comparisonItems.map((item, index) => (
          <Col xs={24} sm={12} lg={6} key={item.key}>
            <NumbersCard
              title={item.title}
              value={item.current}
              suffix={item.suffix}
              color={item.color}
              tooltip={item.tooltip}
              onClick={() => item.api && setSelectedAlert(item)}
              popup={item.api ? true : false}
            />
          </Col>
        ))}
      </Row>

      {/* Charts Section */}
      <Row
        gutter={[16, 16]}
        style={{
          marginTop: "20px",
          flexGrow: 1, // 👈 makes it take remaining space
          display: "flex", // ensure content fills vertically
          flexDirection: "row",
          height: "100%",
        }}
      >
        {/* Comparison Bar Chart */}
        <Col span={12}>
          <Tooltip
            title={trans(
              "Compares current month performance with the previous month."
            )}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                height: "100%",
                // display: "flex", // 👈 make card flex container
                // flexDirection: "column",
              }}
              bodyStyle={{ padding: "16px" }} // 👈 make body fill
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: "13px", fontWeight: "600" }}>
                    {trans("Monthly Comparison")}
                  </span>
                </div>
              }
            >
              <div style={{ height: 380 }}>
                <ReactApexChart
                  options={barChartOptions}
                  series={barSeriesData}
                  type="bar"
                  height={"100%"}
                />
              </div>
            </Card>
          </Tooltip>
        </Col>

        {/* Trend Line Chart */}
        <Col span={12}>
          <Tooltip
            title={trans(
              "Shows performance changes and trends over a period of time"
            )}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                height: "100%",
                // display: "flex", // 👈 make card flex container
                // flexDirection: "column",
              }}
              bodyStyle={{ padding: "16px" }} // 👈 make body fill
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: "13px", fontWeight: "600" }}>
                    {trans("Performance Trends")}
                  </span>
                </div>
              }
            >
              <div style={{ height: 380 }}>
                <ReactApexChart
                  options={lineChartOptions}
                  series={lineSeriesData}
                  type="line"
                  height={"100%"}
                />
              </div>
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Summary Section */}
      <Row style={{ marginTop: "20px" }}>
        <Col span={24}>
          <Tooltip
            title={trans(
              "Summarizes key performance indicators to track overall progress"
            )}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              bodyStyle={{ padding: "16px" }}
              title={
                <span style={{ fontSize: "14px", fontWeight: "600" }}>
                  {trans("Performance Summary")}
                </span>
              }
            >
              <Row gutter={[16, 16]}>
                {comparisonItems.map((item, i) => (
                  <Col span={6} key={i}>
                    <div
                      style={{
                        textAlign: "center",
                        padding: "12px",
                        background:
                          item.difference === 0
                            ? `#FFD8BF88`
                            : (
                                item.isTime
                                  ? item.difference <= 0
                                  : item.difference >= 0
                              )
                            ? "#EAF4EC88" // ✅ green (growing / improving)
                            : "#FFF1F088", // ❌ red (declining)
                        borderRadius: "8px",
                        border: `1px solid ${colorScheme.border}`,
                      }}
                    >
                      <div
                        style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: getTrendColor(
                            item.difference,
                            item.isPositiveGood
                          ),
                          margin: "0 auto 6px",
                        }}
                      />
                      <Text
                        style={{
                          fontSize: "11px",
                          color: colorScheme.dark,
                          fontWeight: "500",
                          display: "block",
                          marginBottom: "3px",
                        }}
                      >
                        {item.title}
                      </Text>
                      <Text
                        style={{
                          fontSize: "10px",
                          color: getTrendColor(
                            item.difference,
                            item.isPositiveGood
                          ),
                          fontWeight: "500",
                        }}
                      >
                        {item.difference === 0
                          ? trans("No change")
                          : item.isTime
                          ? item.difference <= 0
                            ? trans("Improving")
                            : trans("Increasing")
                          : item.difference >= 0
                          ? trans("Growing")
                          : trans("Declining")}
                      </Text>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Tooltip>
        </Col>
      </Row>
      {selectedAlert && (
        <AlertPopup
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
        />
      )}
    </div>
  );
}
